import React, { useState } from "react";
import { CalendarClock, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/auth";
import { useDemoBrand } from "@/lib/demoBrand";

const FRICTION_AUDIT_URL = "https://nexengines.com/#friction-audit";
const FIELDS = [
  { key: "full_name", label: "Full name", type: "text", auto: "name" },
  { key: "email", label: "Email", type: "email", auto: "email" },
  { key: "phone", label: "Phone", type: "tel", auto: "tel" },
  { key: "business_name", label: "Company (optional)", type: "text", auto: "organization" },
  { key: "preferred_time", label: "Preferred time (e.g. Tue afternoon ET)", type: "text", auto: "off" },
];

export default function BookCallForm({ onOpen }) {
  const { slug } = useDemoBrand();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", business_name: "", preferred_time: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/demos/${slug}/book-call`, form);
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? "Please check your email and phone number." : d || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="veroogle-book-call-success" className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-4 animate-in fade-in duration-300">
        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
        <div className="text-sm text-slate-200">You're booked in. We'll reach out to confirm a time shortly.</div>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={() => { onOpen?.(); setOpen(true); }}
          data-testid="veroogle-book-call-open"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-brand-cyan text-navy-950 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase shadow-lg shadow-emerald-500/20 hover:brightness-110 hover:-translate-y-0.5 transition-[filter,transform]"
        >
          <CalendarClock className="w-4 h-4" /> Book a call
        </button>
        <a
          href={FRICTION_AUDIT_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="veroogle-friction-audit-link"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand-cyan transition-colors"
        >
          Or run a free Friction Audit <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="veroogle-book-call-form" className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {FIELDS.map((f) => (
        <input
          key={f.key}
          type={f.type}
          autoComplete={f.auto}
          required={f.key !== "business_name"}
          placeholder={f.label}
          value={form[f.key]}
          onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
          data-testid={`veroogle-book-call-${f.key.replace("_", "-")}`}
          className={`rounded-lg bg-navy-950/70 border border-white/10 focus:border-emerald-400/60 outline-none px-3 py-2 text-sm text-slate-100 ${f.key === "preferred_time" ? "sm:col-span-2" : ""}`}
        />
      ))}
      {error && <div data-testid="veroogle-book-call-error" className="sm:col-span-2 text-xs text-rose-300">{error}</div>}
      <div className="sm:col-span-2 flex items-center justify-end gap-2 mt-1">
        <button type="button" onClick={() => setOpen(false)} data-testid="veroogle-book-call-cancel" className="px-3 py-2 rounded-lg text-slate-300 hover:bg-white/5 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="veroogle-book-call-submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-navy-950 font-mono text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors disabled:opacity-50">
          {busy && <Loader2 className="w-3 h-3 animate-spin" />} Request call
        </button>
      </div>
    </form>
  );
}
